import {
  createFormPageList,
  expandArrayPages,
  isActivePage,
} from 'platform/forms-system/src/js/helpers';

const FIELD_SELECTION_MARKER = 'fieldSelection';

const findFieldSelection = page => {
  const uiSchema = page?.uiSchema || {};
  const scope =
    typeof page?.arrayPath === 'string' && page.arrayPath.length
      ? uiSchema[page.arrayPath]?.items || uiSchema
      : uiSchema;

  const fieldName = Object.keys(scope).find(
    key => scope[key]?.['ui:options']?.[FIELD_SELECTION_MARKER] === true,
  );

  if (!fieldName) return null;

  const { destinationKey } = scope[fieldName]['ui:options'];
  return destinationKey ? { fieldName, destinationKey } : null;
};

const isNil = value => value === undefined || value === null;

const resolveTransform = (transforms, destinationKey) => {
  if (typeof transforms === 'function') return transforms;
  return transforms?.[destinationKey] || (v => v);
};

/**
 * Restores shared-selection field values that forms-system's own submit filtering strips when
 * a selection page goes inactive, and formats every fieldSelection destination value through
 * `transforms` regardless of source — original data, a sibling manual-entry page, or an
 * already-passed-through value. Never deletes data; page activity is read straight from
 * formConfig, the same way transformForSubmit itself determines it.
 *
 * @param {Object} formConfig - the form's formConfig
 * @param {Object} originalData - form.data prior to transformForSubmit
 * @param {Object} transformedData - data after transformForSubmit
 * @param {Record<string, (value: any) => any> | ((value: any) => any)} [transforms] - a function
 *   applied to every destination value, or an object keyed by destinationKey for per-field
 *   transforms (identity for any key omitted). Defaults to identity.
 * @returns {Object} new data object with each destinationKey's value passed through `transforms`
 */
export const rehydrateFieldSelections = (
  formConfig,
  originalData = {},
  transformedData = {},
  transforms = {},
) => {
  const allPages = expandArrayPages(
    createFormPageList(formConfig),
    originalData,
  );

  const selectionPages = allPages
    .map(page => {
      const selection = findFieldSelection(page);
      return selection ? { page, ...selection } : null;
    })
    .filter(Boolean);

  return selectionPages.reduce((result, { page, destinationKey }) => {
    const transform = resolveTransform(transforms, destinationKey);
    const active = isActivePage(page, originalData);

    // --- array-builder item field ---
    if (typeof page.arrayPath === 'string' && page.arrayPath.length) {
      const items = result[page.arrayPath];
      if (!Array.isArray(items) || items[page.index] === undefined) {
        return result;
      }

      const currentItem = items[page.index];
      const sourceValue = active
        ? originalData?.[page.arrayPath]?.[page.index]?.[destinationKey]
        : currentItem[destinationKey];

      const value = transform(sourceValue);
      if (isNil(value)) return result;

      const nextItems = [...items];
      nextItems[page.index] = { ...currentItem, [destinationKey]: value };
      return { ...result, [page.arrayPath]: nextItems };
    }

    // --- top-level field ---
    const sourceValue = active
      ? originalData?.[destinationKey]
      : result[destinationKey];

    const value = transform(sourceValue);
    if (isNil(value)) return result;

    return { ...result, [destinationKey]: value };
  }, transformedData);
};
